import { useQuery } from '@tanstack/react-query'
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { type ReactNode, useEffect } from 'react'
import { failureFromThrown } from '../lib/api-error.ts'
import { readPlatformIdentity } from '../lib/gateway.ts'
import { messageForCode } from '../lib/messages.ts'
import { useSession } from '../session/context.tsx'
import { Masthead } from './masthead.tsx'
import { Notice } from './notice.tsx'

/**
 * The signed-in layout: the masthead, and below it the page (§5.1).
 *
 * It also owns the two facts every screen inside it depends on.
 *
 * **Who is signed in.** Nothing is persisted (docs/DECISIONS.md, "Platform-admin tokens
 * live in memory only"), so restoring a session is *re-reading* it: while the store holds
 * tokens this asks `GET /platform/auth/session` who they belong to, and the answer is the
 * database's current one rather than anything the token claimed.
 *
 * **When there is no session.** A route reached while signed out redirects to `/login`,
 * remembering where the owner was going, and renders nothing on the way — never a flash
 * of a table (§18.2). The redirect lives here as well as in the route guard because a
 * session can end *underneath* a page that is already open.
 *
 * **This is the only redirect a session's end produces.** The masthead's Sign out ends
 * the session and navigates nowhere: two redirects to `/login` raced over `?next=`, and
 * the one that landed last decided where the following sign-in went. So the decision
 * about what to remember is made once, here, from *why* the session ended.
 */
export const SESSION_QUERY_KEY = ['platformSession'] as const

export type AppShellProps = {
  /**
   * The page to draw instead of the matched route's. Only the router's own not-found page uses it:
   * an address no route matches has no route in the signed-in group to be an `Outlet` of, and it
   * still belongs inside the masthead and the environment rail (§5.1, §18.3).
   */
  readonly page?: ReactNode
}

export function AppShell({ page }: AppShellProps = {}) {
  const session = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const signedIn = session.tokens !== undefined
  /** §4.4: a deliberate sign-out is not an interruption, so there is nothing to resume. */
  const endedDeliberately = session.endedReason === 'signedOut'

  useEffect(() => {
    // Already on the way: the redirect below changes `location.pathname` to `/login`,
    // which re-runs this effect before the shell unmounts. Returning here rather than
    // navigating again is what lets `next` survive — the second run used to replace the
    // search it had just written, so the route was remembered and then immediately
    // forgotten (§18.2 wants it remembered **once**, not zero times). It also removes
    // the loop this guard has always existed to prevent.
    if (signedIn || location.pathname === '/login') {
      return
    }
    void navigate({
      to: '/login',
      // Remembered once, and resumed after signing in (§18.2) — except after a
      // deliberate sign-out, which is not an interruption to resume (§4.4).
      search: { next: endedDeliberately ? undefined : location.pathname },
      replace: true,
    })
  }, [signedIn, endedDeliberately, navigate, location.pathname])

  const identity = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: readPlatformIdentity,
    enabled: signedIn,
  })

  if (!signedIn) {
    return null
  }

  return (
    <div className="shell">
      <Masthead nickname={identity.data?.nickname} />
      {identity.isPending ? (
        // Structure first (§18.1): the chrome is already on screen and the page area
        // holds the shape of what is coming, rather than a spinner centred over nothing.
        // The word is announced rather than drawn — the placeholders are what a sighted
        // reader gets, and they carry no text worth reading aloud.
        <main className="page" aria-busy="true">
          <p className="visually-hidden">Loading…</p>
          <div aria-hidden="true">
            <span className="skeleton skeleton-title" />
            <span className="skeleton skeleton-wide" />
            <span className="skeleton skeleton-wide" />
            <span className="skeleton skeleton-narrow" />
          </div>
        </main>
      ) : identity.isError ? (
        <main className="page">
          <div className="panel panel-error">
            <h2>This page could not be loaded</h2>
            <Notice tone="failure">{messageForCode(failureFromThrown(identity.error).code)}</Notice>
            <button
              type="button"
              className="button"
              onClick={() => {
                void identity.refetch()
              }}
            >
              Try again
            </button>
          </div>
        </main>
      ) : (
        (page ?? <Outlet />)
      )}
    </div>
  )
}
